/**
 * Chi occupa gli estrusori, per stampo.
 *
 * Lo stampo (la trafila) è **il driver del costo di setup**: il doc #2 lo dice
 * con un dato — la ricetta cambia quasi sempre, quindi non è una leva, mentre gli
 * stampi sono 14 e cambiarli costa 40 minuti. Nonostante questo nessuna
 * schermata diceva quali trafile si portano via gli estrusori.
 *
 * Sui dati veri **T006 da solo è il 61% dei chili estrusi**: è la stessa forma
 * della classifica «chi tiene ferma la merce» in Commesse — un numero che
 * esisteva riga per riga e che nessuno aveva mai sommato.
 */
import { coloreStampo, num } from "@/pages/M2/format";
import type { QuotaStampoRead } from "@/types/m2";

const fmt = (n: number) => num(Math.round(n));

export function ClassificaStampi({
  voci,
  onApri,
}: {
  voci: QuotaStampoRead[];
  /** Il clic filtra il Piano su quello stampo: anche questa riga è una porta. */
  onApri?: (stampo: string) => void;
}) {
  if (!voci.length) return null;

  return (
    <section>
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Chi occupa gli estrusori
      </h2>
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Stampo (trafila)</th>
                <th className="px-3 py-2 text-right font-medium">Lavorazioni</th>
                <th className="px-3 py-2 text-right font-medium">Chilogrammi</th>
                <th className="w-[38%] px-3 py-2 text-left font-medium">Quota dell&apos;estrusione</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {voci.map((q) => {
                const c = coloreStampo(q.stampo_code);
                const cliccabile = Boolean(q.stampo_code && onApri);
                return (
                  <tr
                    key={q.stampo_code ?? "ignoto"}
                    className={cliccabile ? "cursor-pointer hover:bg-accent/40" : undefined}
                    onClick={cliccabile ? () => onApri?.(q.stampo_code!) : undefined}
                    title={cliccabile ? "Apre il piano filtrato su questo stampo" : undefined}
                  >
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="size-2.5 shrink-0 rounded-sm"
                          style={{ background: c.bg, border: `1.5px solid ${c.border}` }}
                        />
                        <span className="font-mono text-[11.5px]">
                          {q.stampo_code ?? (
                            <span className="font-sans italic text-muted-foreground">
                              non riconosciuto
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      {q.lavorazioni}
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                      {fmt(q.kg)} kg
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="h-1.5 w-full overflow-hidden rounded-sm bg-muted">
                        <span
                          className="block h-full"
                          style={{
                            width: `${Math.min(100, q.quota_pct)}%`,
                            background: c.border,
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {Math.round(q.quota_pct)}% dei chili estrusi
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Cambiare trafila costa ~40 minuti di macchina ferma; cambiare ricetta a trafila uguale
        ~10. Raggruppare per stampo è la leva vera, ed è quella che prova la Simulazione.
      </p>
    </section>
  );
}
